import { X } from "lucide-react";
import CategoryTree from "./CategoryTree";

const FLAG_LABELS = {
  quote_required: "Quote required", bad_code: "Bad code",
  no_code: "No code", duplicate: "Duplicate", stale_catalog: "Stale catalog",
};

function Section({ title, children }) {
  return (
    <div className="border-t border-slate-200 pt-3 mt-3 first:border-0 first:pt-0 first:mt-0">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">{title}</div>
      {children}
    </div>
  );
}

function Pill({ label, count, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between rounded px-2 py-1 text-[11px] transition-colors ${
        active ? "bg-[#007BFF]/10 text-[#007BFF] font-medium" : "text-slate-600 hover:bg-slate-100"
      }`}
      data-testid={testid}
    >
      <span className="truncate">{label}</span>
      {count !== undefined && <span className="text-slate-400 tabular-nums">{count?.toLocaleString()}</span>}
    </button>
  );
}

export default function FilterPanel({ facets, filters, setFilter, resetFilters }) {
  const activeCount = Object.entries(filters).filter(([, v]) => v !== "" && v !== null).length;

  return (
    <aside className="w-full md:w-[240px] shrink-0 border-b md:border-b-0 md:border-r border-slate-200 bg-white overflow-y-auto px-3 py-2"
           data-testid="price-list-filter-panel">
      {activeCount > 0 && (
        <button onClick={resetFilters}
                className="mt-2 flex items-center gap-1 text-[11px] text-slate-500 hover:text-red-600"
                data-testid="filter-clear-all">
          <X className="h-3 w-3" /> Clear {activeCount} filter{activeCount === 1 ? "" : "s"}
        </button>
      )}

      <Section title="Cost Code">
        <CategoryTree categories={facets?.categories} selected={filters.category}
                      onSelect={(p) => setFilter("category", p)} />
      </Section>

      <Section title="Flags">
        {Object.entries(FLAG_LABELS).map(([k, label]) => (
          <Pill key={k} label={label} count={facets?.flags?.[k]} active={filters.flag === k}
                onClick={() => setFilter("flag", filters.flag === k ? "" : k)}
                testid={`filter-flag-${k}`} />
        ))}
      </Section>

      <Section title="Status">
        <Pill label="Active" count={facets?.active} active={filters.active === "true"}
              onClick={() => setFilter("active", filters.active === "true" ? "" : "true")}
              testid="filter-active" />
        <Pill label="Inactive" count={facets?.inactive} active={filters.active === "false"}
              onClick={() => setFilter("active", filters.active === "false" ? "" : "false")}
              testid="filter-inactive" />
      </Section>

      <Section title="Manufacturer">
        <select value={filters.manufacturer} onChange={(e) => setFilter("manufacturer", e.target.value)}
                className="h-8 w-full rounded-md border border-slate-200 px-1.5 text-[11px]"
                data-testid="filter-manufacturer">
          <option value="">All manufacturers</option>
          {(facets?.manufacturers || []).map((m) => (
            <option key={m.name} value={m.name === "(none)" ? "" : m.name}>
              {m.name} ({m.count})
            </option>
          ))}
        </select>
      </Section>

      <Section title="Cost Code #">
        <select value={filters.cost_code_id} onChange={(e) => setFilter("cost_code_id", e.target.value)}
                className="h-8 w-full rounded-md border border-slate-200 px-1.5 text-[11px]"
                data-testid="filter-cost-code">
          <option value="">All cost code numbers</option>
          {(facets?.cost_codes || []).map((c) => (
            <option key={c.id} value={c.id}>{c.code} ({c.count})</option>
          ))}
        </select>
      </Section>
    </aside>
  );
}
